import React from 'react';
import { X } from 'lucide-react';

import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';

/**
 * Look at one image, full size.
 *
 * Deliberately not {@link GenerationLightbox}: that one is typed to a CsAsset
 * and exists to make a GENERATED image awkward to save — right-click blocked,
 * drag blocked. A reference the vendor uploaded is their own picture, so
 * protecting it from them is pointless, and reusing that component would mean
 * inventing a fake asset to satisfy its props.
 *
 * What matters here is only that the thing is big enough to judge: a 52×40
 * thumbnail cannot answer "is this the lighting I meant?", which is the whole
 * reason a reference is attached.
 */
export const ImagePreviewDialog: React.FC<{
    url: string | null;
    alt?: string;
    onClose: () => void;
}> = ({ url, alt, onClose }) => (
    <Dialog open={!!url} onOpenChange={(next) => { if (!next) onClose(); }}>
        <DialogContent
            // The image IS the dialog — no paper, no padding, nothing to frame it.
            showCloseButton={false}
            className="w-auto max-w-none bg-transparent p-0 ring-0 sm:max-w-none"
            // Darker than the shared scrim: the page must be gone, not dimmed,
            // when someone is judging a generated image against it.
            overlayClassName="bg-black/70 supports-backdrop-filter:backdrop-blur-sm"
        >
            {/* Radix needs a name for the dialog; there is nothing to show one
                on, so it is announced but not drawn. */}
            <DialogTitle className="sr-only">{alt || 'Preview image'}</DialogTitle>
            <div
                // The studio dialog behind this one stops propagation on everything;
                // a click here must not reach the canvas and drag a node.
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                className="relative leading-none"
            >
                {url && (
                    // Fits the viewport in both directions at its own aspect
                    // ratio, however tall or wide the reference happens to be.
                    <img src={url} alt={alt || ''} className="block max-h-[88vh] max-w-[92vw]" />
                )}
                <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close"
                    className="absolute -top-3.5 -end-3.5 flex size-8 items-center justify-center rounded-full bg-background text-foreground"
                >
                    <X className="size-4" />
                </button>
            </div>
        </DialogContent>
    </Dialog>
);
